import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Loader2,
  Globe,
  Download,
  Film,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useThemeStore } from '../../store/themeStore';
import { analyticsAPI, getErrorMessage } from '../../services/api';

/* ─── Types ─── */
interface StatItem {
  label: string;
  value: string;
  raw_value: number;
  trend: number;
  icon: string;
}

interface TimelinePoint {
  date: string;
  date_raw: string;
  likes: number;
  comments: number;
  shares: number;
  reach: number;
}

interface PlatformDist {
  name: string;
  value: number;
  percentage: number;
  color: string;
}

interface ContentPerf {
  type: string;
  avg_engagement: number;
  total_engagement: number;
  count: number;
}

interface TopPost {
  id: string;
  text: string;
  published_at: string | null;
  platforms: string[];
  media_type: string;
  likes: number;
  comments: number;
  shares: number;
  reach: number;
  engagement: number;
}

interface BestTime {
  day?: string;
  time?: string;
  platform?: string;
  avg_engagement?: number;
  sample_size?: number;
  reason?: string;
}

type ValidBestTime = BestTime & {
  day: string;
  time: string;
};

type Period = '7d' | '30d' | '90d' | '1y';

const PLATFORM_COLORS: Record<string, string> = {
  instagram: '#E1306C',
  facebook: '#1877F2',
  twitter: '#1DA1F2',
  linkedin: '#0A66C2',
  tiktok: '#000000',
  youtube: '#FF0000',
  pinterest: '#E60023',
  threads: '#000000',
};

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/* ─── Component ─── */
const AnalyticsPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const pc = theme.hex?.primary || '#10b981';
  const queryClient = useQueryClient();

  const [timePeriod, setTimePeriod] = useState<Period>('30d');
  const [selectedPlatform, setSelectedPlatform] = useState('All');
  const [activeTab, setActiveTab] = useState<'dashboard' | 'profile' | 'audience' | 'posts' | 'stories'>('dashboard');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  /* ─── Queries ─── */
  const { data: overview, isLoading: loadingOverview } = useQuery({
    queryKey: ['analytics-overview', timePeriod],
    queryFn: async () => {
      const { data } = await analyticsAPI.overview(timePeriod);
      return data as {
        stats: StatItem[];
        engagement: Record<string, number>;
        posts: Record<string, number>;
        connected_accounts: number;
      };
    },
  });

  const { data: timeline, isLoading: loadingTimeline } = useQuery({
    queryKey: ['analytics-timeline', timePeriod, selectedPlatform],
    queryFn: async () => {
      const { data } = await analyticsAPI.engagementTimeline({
        period: timePeriod,
        platform: selectedPlatform !== 'All' ? selectedPlatform : undefined,
      });
      return data as { timeline: TimelinePoint[] };
    },
  });

  const { data: platformDist } = useQuery({
    queryKey: ['analytics-platform-dist', timePeriod],
    queryFn: async () => {
      const { data } = await analyticsAPI.platformDistribution(timePeriod);
      return data as { distribution: PlatformDist[]; total_posts: number };
    },
  });

  const { data: contentPerf } = useQuery({
    queryKey: ['analytics-content-perf', timePeriod],
    queryFn: async () => {
      const { data } = await analyticsAPI.contentPerformance(timePeriod);
      return data as { performance: ContentPerf[] };
    },
  });

  const { data: topPosts } = useQuery({
    queryKey: ['analytics-top-posts', timePeriod, selectedPlatform],
    queryFn: async () => {
      const { data } = await analyticsAPI.posts({
        period: timePeriod,
        platform: selectedPlatform !== 'All' ? selectedPlatform : undefined,
        sort_by: 'engagement',
      });
      return data as { posts: TopPost[]; total_posts: number };
    },
  });

  const { data: bestTimes } = useQuery({
    queryKey: ['analytics-best-times'],
    queryFn: async () => {
      const { data } = await analyticsAPI.bestTimes();
      return data as { recommendations: BestTime[] };
    },
  });

  const { data: profileData } = useQuery({
    queryKey: ['analytics-profile', timePeriod],
    queryFn: () => analyticsAPI.profile(timePeriod).then((r) => r.data),
    staleTime: 60_000,
    enabled: activeTab === 'profile',
  });

  const { data: audienceData } = useQuery({
    queryKey: ['analytics-audience'],
    queryFn: () => analyticsAPI.audience().then((r) => r.data),
    staleTime: 60_000,
    enabled: activeTab === 'audience',
  });

  const { data: storiesData } = useQuery({
    queryKey: ['analytics-stories', timePeriod],
    queryFn: () => analyticsAPI.stories(timePeriod).then((r) => r.data),
    staleTime: 60_000,
    enabled: activeTab === 'stories',
  });

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: () => analyticsAPI.sync(),
    onSuccess: () => {
      toast.success(t('analytics_msg_sync_complete', 'Engagement sync complete'));
      queryClient.invalidateQueries({ queryKey: ['analytics-overview'] });
      queryClient.invalidateQueries({ queryKey: ['analytics-timeline'] });
      queryClient.invalidateQueries({ queryKey: ['analytics-top-posts'] });
      queryClient.invalidateQueries({ queryKey: ['analytics-content-perf'] });
    },
    onError: () => toast.error(t('analytics_msg_sync_failed', 'Sync failed')),
  });

  const bestTimeRecommendations = useMemo<ValidBestTime[]>(() => {
    return (bestTimes?.recommendations || []).filter(
      (recommendation): recommendation is ValidBestTime =>
        typeof recommendation?.day === 'string' && typeof recommendation?.time === 'string',
    );
  }, [bestTimes?.recommendations]);

  /* ─── Heatmap from best-times ─── */
  const heatmapData: number[][] = (() => {
    const grid: number[][] = DAY_ORDER.map(() => new Array(24).fill(0));
    if (bestTimeRecommendations.length > 0) {
      // Find max engagement for normalization
      const maxEng = Math.max(...bestTimeRecommendations.map((r) => r.avg_engagement || 0), 1);
      for (const r of bestTimeRecommendations) {
        const dayIdx = DAY_ORDER.indexOf(r.day);
        const hour = Number.parseInt(r.time.split(':')[0], 10);
        if (dayIdx >= 0 && Number.isFinite(hour) && hour >= 0 && hour < 24) {
          grid[dayIdx][hour] = Math.round(((r.avg_engagement || 0) / maxEng) * 100);
        }
      }
    }
    return grid;
  })();

  const hexToRgb = (hex: string): string => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : '16, 185, 129';
  };

  const getHeatmapColor = (value: number): string => {
    const rgb = hexToRgb(pc);
    const intensity = value / 100;
    if (intensity < 0.01) return `rgba(${rgb}, 0.05)`;
    if (intensity < 0.25) return `rgba(${rgb}, 0.15)`;
    if (intensity < 0.5) return `rgba(${rgb}, 0.35)`;
    if (intensity < 0.75) return `rgba(${rgb}, 0.6)`;
    return `rgba(${rgb}, 1)`;
  };

  const hourLabels = Array.from({ length: 24 }, (_, i) => `${i}:00`);

  /* ─── Available platforms from connected accounts ─── */
  const platforms = ['All'];
  if (platformDist?.distribution) {
    for (const d of platformDist.distribution) {
      if (!platforms.includes(d.name)) platforms.push(d.name);
    }
  }
  // Always show the common ones
  for (const p of ['Instagram', 'Facebook', 'Twitter', 'LinkedIn', 'TikTok']) {
    if (!platforms.includes(p)) platforms.push(p);
  }

  /* ─── Loading skeleton ─── */
  const Skeleton = ({ className = '' }: { className?: string }) => (
    <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
  );

  // Close export menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showExportMenu]);

  /* ─── Export CSV function ─── */
  const handleExportCSV = async () => {
    try {
      setIsExporting(true);
      const format = 'csv';
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${import.meta.env.VITE_API_URL || ""}/api/analytics/export?format=${format}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        throw new Error('Export failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t('analytics_export_csv_success', 'CSV exported successfully'));
      setShowExportMenu(false);
    } catch (error) {
      toast.error(getErrorMessage(error, t('analytics_export_failed', 'Export failed')));
    } finally {
      setIsExporting(false);
    }
  };

  /* ─── Export PDF function ─── */
  const handleExportPDF = async () => {
    try {
      setIsExporting(true);
      const format = 'pdf';
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${import.meta.env.VITE_API_URL || ""}/api/analytics/export?format=${format}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        throw new Error('Export failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_export_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t('analytics_export_pdf_success', 'PDF exported successfully'));
      setShowExportMenu(false);
    } catch (error) {
      toast.error(getErrorMessage(error, t('analytics_export_failed', 'Export failed')));
    } finally {
      setIsExporting(false);
    }
  };

  const tabs = useMemo(() => [
    { id: 'dashboard' as const, label: t('analytics_tab_dashboard', 'Dashboard') },
    { id: 'profile' as const, label: t('analytics_tab_profile', 'Profile') },
    { id: 'audience' as const, label: t('analytics_tab_audience', 'Audience') },
    { id: 'posts' as const, label: t('analytics_tab_posts', 'Posts') },
    { id: 'stories' as const, label: t('analytics_tab_stories', 'Stories') },
  ], [t]);

  const storiesSummary = useMemo(() => [
    { label: t('analytics_label_total_stories', 'Total Stories'), value: storiesData?.total_stories || 0, color: 'blue' },
    { label: t('analytics_label_total_views', 'Total Views'), value: storiesData?.total_views || 0, color: 'purple' },
    { label: t('analytics_label_total_replies', 'Total Replies'), value: storiesData?.total_replies || 0, color: 'emerald' },
    { label: t('analytics_label_avg_views_story', 'Avg Views/Story'), value: storiesData?.avg_views_per_story || 0, color: 'amber' },
  ], [storiesData, t]);

  return (
    <div className="bg-gray-100 min-h-full">
      <div className="px-4 pt-3 pb-4">
        {/* Toolbar */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {/* Tabs */}
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-2.5 py-1.5 rounded-lg font-medium text-xs transition-colors ${
                activeTab === tab.id
                  ? 'text-white'
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
              style={activeTab === tab.id ? { backgroundColor: pc } : undefined}
            >
              {tab.label}
            </button>
          ))}

          <div className="w-px h-5 bg-gray-300 mx-1" />

          {/* Period pills */}
          {(['7d', '30d', '90d', '1y'] as const).map((period) => (
            <button
              key={period}
              onClick={() => setTimePeriod(period)}
              className={`px-2 py-1 rounded-lg font-medium text-[10px] transition-colors ${
                timePeriod === period
                  ? 'text-white'
                  : 'text-gray-500 hover:bg-gray-200'
              }`}
              style={timePeriod === period ? { backgroundColor: pc } : undefined}
            >
              {period === '7d' ? '7D' : period === '30d' ? '30D' : period === '90d' ? '90D' : '1Y'}
            </button>
          ))}

          <div className="w-px h-5 bg-gray-300 mx-1" />

          {/* Platform filter */}
          {platforms.map((platform) => (
            <button
              key={platform}
              onClick={() => setSelectedPlatform(platform)}
              className={`px-2 py-1 rounded-lg text-[10px] font-semibold transition-colors ${
                selectedPlatform === platform
                  ? 'text-white'
                  : 'text-gray-500 hover:bg-gray-200'
              }`}
              style={selectedPlatform === platform ? { backgroundColor: pc } : undefined}
            >
              {platform}
            </button>
          ))}

          <div className="flex-1" />

          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={isExporting}
              className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-white transition-colors disabled:opacity-60"
            >
              <Download className="h-3 w-3" />
              {t('analytics_btn_export', 'Export')}
            </button>
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg border border-gray-200 shadow-lg z-50">
                <button
                  onClick={handleExportCSV}
                  disabled={isExporting}
                  className="w-full text-left px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-60 rounded-t-lg transition-colors"
                >
                  {isExporting ? '...' : t('analytics_export_csv', 'Export CSV')}
                </button>
                <div className="border-t border-gray-200" />
                <button
                  onClick={handleExportPDF}
                  disabled={isExporting}
                  className="w-full text-left px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-60 rounded-b-lg transition-colors"
                >
                  {isExporting ? '...' : t('analytics_export_pdf', 'Export PDF')}
                </button>
              </div>
            )}
          </div>
          <button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 text-white rounded-lg font-medium text-xs transition-colors hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: pc }}
          >
            {syncMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            {t('analytics_btn_sync', 'Sync')}
          </button>
        </div>

        {activeTab === 'dashboard' && (
          <>
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          {loadingOverview
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl p-3 border border-gray-200">
                  <Skeleton className="h-3 w-16 mb-2" />
                  <Skeleton className="h-5 w-12" />
                </div>
              ))
            : overview?.stats.map((stat, idx) => {
                const borderColors = ['border-l-blue-500', 'border-l-emerald-500', 'border-l-purple-500', 'border-l-amber-500'];
                return (
                <div key={idx} className={`bg-white rounded-xl p-3 border border-gray-200 border-l-[3px] ${borderColors[idx % 4]}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">{stat.label}</p>
                    <div className="flex items-center gap-0.5">
                      {stat.trend >= 0 ? (
                        <TrendingUp size={11} className="text-green-500" />
                      ) : (
                        <TrendingDown size={11} className="text-red-500" />
                      )}
                      <span className={`text-[10px] font-bold ${stat.trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {stat.trend > 0 ? '+' : ''}{stat.trend}%
                      </span>
                    </div>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{stat.value}</p>
                </div>
                );
              })}
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
          {/* Engagement Over Time */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 lg:col-span-2">
            <h3 className="text-xs font-semibold text-gray-900 mb-3">{t('analytics_chart_engagement_time', 'Engagement Over Time')}</h3>
            {loadingTimeline ? (
              <Skeleton className="h-[300px] w-full" />
            ) : !timeline?.timeline?.length ? (
              <div className="h-[300px] flex items-center justify-center text-gray-400">
                {t('analytics_msg_no_engagement_data', 'No engagement data for this period')}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={timeline.timeline}>
                  <defs>
                    <linearGradient id="colorLikes" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={pc} stopOpacity={0.4} />
                      <stop offset="95%" stopColor={pc} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorComments" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorShares" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke="#9ca3af"
                    tick={{ fontSize: 12 }}
                    interval={timePeriod === '7d' ? 0 : timePeriod === '90d' ? 6 : timePeriod === '1y' ? 29 : 2}
                  />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: `1px solid ${pc}`,
                      borderRadius: '12px',
                      color: '#374151',
                      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.15)',
                    }}
                  />
                  <Area type="monotone" dataKey="likes" stroke={pc} strokeWidth={2} fillOpacity={1} fill="url(#colorLikes)" name="Likes" />
                  <Area type="monotone" dataKey="comments" stroke="#f59e0b" strokeWidth={2} fillOpacity={1} fill="url(#colorComments)" name="Comments" />
                  <Area type="monotone" dataKey="shares" stroke="#8b5cf6" strokeWidth={2} fillOpacity={1} fill="url(#colorShares)" name="Shares" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Posts by Platform */}
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <h3 className="text-xs font-semibold text-gray-900 mb-3">{t('analytics_chart_posts_platform', 'Posts by Platform')}</h3>
            {!platformDist?.distribution?.length ? (
              <div className="h-[300px] flex items-center justify-center text-gray-400">
                {t('analytics_msg_no_posts', 'No published posts yet')}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={platformDist.distribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name} (${percentage}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {(platformDist?.distribution || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: `1px solid ${pc}`,
                      borderRadius: '12px',
                      color: '#374151',
                      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.15)',
                    }}
                    formatter={(value: number, name: string) => [`${value} posts`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Content Type Performance */}
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <h3 className="text-xs font-semibold text-gray-900 mb-3">{t('analytics_chart_content_perf', 'Content Type Performance')}</h3>
            {!contentPerf?.performance?.length ? (
              <div className="h-[300px] flex items-center justify-center text-gray-400">
                {t('analytics_msg_no_perf_data', 'No performance data yet')}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={contentPerf.performance}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis dataKey="type" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: `1px solid ${pc}`,
                      borderRadius: '12px',
                      color: '#374151',
                      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.15)',
                    }}
                    formatter={(value: number, name: string) => [
                      value.toLocaleString(),
                      name === 'avg_engagement' ? t('analytics_label_avg_engagement', 'Avg Engagement') : name,
                    ]}
                  />
                  <Bar dataKey="avg_engagement" fill={pc} radius={[8, 8, 0, 0]} name={t('analytics_label_avg_engagement', 'Avg Engagement')} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Best Posting Times Heatmap */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 mb-3">
          <h3 className="text-xs font-semibold text-gray-900 mb-3">{t('analytics_chart_best_times', 'Best Posting Times')}</h3>
          {!bestTimeRecommendations.length ? (
            <div className="h-32 flex items-center justify-center text-gray-400">
              {t('analytics_msg_publish_more', 'Publish more posts to unlock personalized timing insights')}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <div className="inline-block min-w-full">
                  <div className="flex mb-2">
                    <div className="w-16"></div>
                    {hourLabels.filter((_, i) => i % 2 === 0).map((hour) => (
                      <div key={hour} className="flex-1 text-center text-xs font-bold text-gray-700" style={{ minWidth: '2rem' }}>
                        {hour}
                      </div>
                    ))}
                  </div>
                  {DAY_SHORT.map((day, dayIdx) => (
                    <div key={day} className="flex items-center gap-2">
                      <div className="w-16 text-sm font-bold text-gray-700">{day}</div>
                      <div className="flex gap-0.5 flex-1">
                        {heatmapData[dayIdx].map((value, hourIdx) => (
                          <div
                            key={`${dayIdx}-${hourIdx}`}
                            className="flex-1 h-7 rounded-sm transition-all hover:scale-125 cursor-pointer shadow-sm"
                            style={{ backgroundColor: getHeatmapColor(value) }}
                            title={`${DAY_ORDER[dayIdx]} ${hourLabels[hourIdx]}: ${value > 0 ? `${value}% engagement` : 'No data'}`}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-6 flex items-center gap-3 text-sm">
                <span className="font-semibold text-gray-700">{t('analytics_label_low', 'Low')}</span>
                {[0.05, 0.15, 0.35, 0.6, 1].map((opacity, idx) => {
                  const rgb = hexToRgb(pc);
                  return (
                    <div
                      key={idx}
                      className="w-5 h-5 rounded-sm shadow-sm"
                      style={{ backgroundColor: `rgba(${rgb}, ${opacity})` }}
                    />
                  );
                })}
                <span className="font-semibold text-gray-700">{t('analytics_label_high', 'High')}</span>
              </div>
            </>
          )}
        </div>

        {/* Top Performing Posts - Dashboard Tab */}
        {activeTab === 'dashboard' && (
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <h3 className="text-xs font-semibold text-gray-900 mb-3">
            {t('analytics_chart_top_posts', 'Top Performing Posts')}
            {topPosts?.total_posts ? (
              <span className="text-[10px] font-normal text-gray-400 ml-2">
                ({topPosts.total_posts} {t('analytics_label_published', 'published')})
              </span>
            ) : null}
          </h3>
          {!topPosts?.posts?.length ? (
            <div className="py-12 text-center text-gray-400">
              {t('analytics_msg_no_posts_period', 'No published posts in this period')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">#</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">{t('analytics_col_content', 'Content')}</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">{t('analytics_col_platform', 'Platform')}</th>
                    <th className="px-2 py-2 text-right text-[10px] font-semibold text-gray-500 uppercase">{t('analytics_col_likes', 'Likes')}</th>
                    <th className="px-2 py-2 text-right text-[10px] font-semibold text-gray-500 uppercase">{t('analytics_col_comments', 'Comments')}</th>
                    <th className="px-2 py-2 text-right text-[10px] font-semibold text-gray-500 uppercase">{t('analytics_col_shares', 'Shares')}</th>
                    <th className="px-2 py-2 text-right text-[10px] font-semibold text-gray-500 uppercase">{t('analytics_col_reach', 'Reach')}</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">{t('analytics_col_date', 'Date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(topPosts?.posts || []).map((post, idx) => (
                    <tr key={post.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="px-2 py-2">
                        <div
                          className="flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-bold"
                          style={{ backgroundColor: idx < 3 ? pc : '#d1d5db' }}
                        >
                          {idx + 1}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-900 max-w-xs truncate">
                        {post.text || t('analytics_msg_no_text', '(no text)')}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex gap-1">
                          {post.platforms.map((p) => (
                            <span
                              key={p}
                              className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold text-white"
                              style={{ backgroundColor: PLATFORM_COLORS[p.toLowerCase()] || '#6b7280' }}
                            >
                              {p}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-xs font-semibold text-gray-900 text-right">
                        {post.likes.toLocaleString()}
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-600 text-right">
                        {post.comments.toLocaleString()}
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-600 text-right">
                        {post.shares.toLocaleString()}
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-600 text-right">
                        {post.reach.toLocaleString()}
                      </td>
                      <td className="px-2 py-2 text-[10px] text-gray-500">
                        {post.published_at
                          ? new Date(post.published_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}
      </>
        )}

        {/* Profile Tab Content */}
        {activeTab === 'profile' && (
          <div className="space-y-3">
            {/* Follower Summary Cards */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-xl border border-gray-200 p-3 border-l-[3px] border-l-blue-500">
                <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">{t('analytics_label_total_followers', 'Total Followers')}</p>
                <p className="text-lg font-bold text-gray-900">{(profileData?.total_followers || 0).toLocaleString()}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-3 border-l-[3px] border-l-purple-500">
                <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">{t('analytics_label_total_following', 'Total Following')}</p>
                <p className="text-lg font-bold text-gray-900">{(profileData?.total_following || 0).toLocaleString()}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-3 border-l-[3px] border-l-emerald-500">
                <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">{t('analytics_label_accounts_connected', 'Accounts Connected')}</p>
                <p className="text-lg font-bold text-gray-900">{profileData?.accounts?.length || 0}</p>
              </div>
            </div>

            {/* Follower Growth Chart */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-xs font-semibold text-gray-900 mb-3">{t('analytics_chart_follower_growth', 'Follower Growth')}</h3>
              {profileData?.follower_timeline?.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={profileData.follower_timeline}>
                    <defs>
                      <linearGradient id="followerGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.hex.primary} stopOpacity={0.2} />
                        <stop offset="95%" stopColor={theme.hex.primary} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Area type="monotone" dataKey="followers" stroke={theme.hex.primary} fill="url(#followerGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-gray-400 text-center py-12">{t('analytics_msg_no_follower_data', 'No follower data available yet')}</p>
              )}
            </div>

            {/* Connected Accounts List */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-xs font-semibold text-gray-900 mb-3">{t('analytics_section_connected_accounts', 'Connected Accounts')}</h3>
              <div className="divide-y divide-gray-100">
                {(profileData?.accounts || []).map((acct: { account_id: string; avatar_url?: string; platform?: string; display_name?: string; username?: string; followers?: number }) => (
                  <div key={acct.account_id} className="flex items-center gap-2.5 py-2 hover:bg-gray-50 transition-colors">
                    {acct.avatar_url ? (
                      <img src={acct.avatar_url} alt="" className="w-7 h-7 rounded-full" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-[10px] text-gray-500">{acct.platform?.[0]?.toUpperCase()}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-900 truncate">{acct.display_name}</p>
                      <p className="text-[10px] text-gray-400">@{acct.username} · {acct.platform}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-bold text-gray-900">{(acct.followers || 0).toLocaleString()}</p>
                      <p className="text-[10px] text-gray-400">{t('analytics_label_followers', 'followers')}</p>
                    </div>
                  </div>
                ))}
                {(!profileData?.accounts || profileData.accounts.length === 0) && (
                  <p className="text-sm text-gray-400 text-center py-8">{t('analytics_msg_no_accounts', 'No accounts connected')}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Audience Tab Content */}
        {activeTab === 'audience' && (
          <div className="space-y-3">
            {/* Gender Distribution */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-xs font-semibold text-gray-900 mb-3">{t('analytics_chart_gender_dist', 'Gender Distribution')}</h3>
                {audienceData?.gender?.length > 0 ? (
                  <div className="space-y-3">
                    {(audienceData?.gender || []).map((g: { label: string; percentage: number }) => (
                      <div key={g.label} className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-gray-600 w-16">{g.label}</span>
                        <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${g.percentage}%`,
                              backgroundColor: g.label === 'Male' ? '#3b82f6' : g.label === 'Female' ? '#ec4899' : '#8b5cf6',
                            }}
                          />
                        </div>
                        <span className="text-xs font-bold text-gray-700 w-12 text-right">{g.percentage}%</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-8">{t('analytics_msg_no_gender_data', 'No gender data available. Connect Instagram Business or Facebook Page to see audience demographics.')}</p>
                )}
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-xs font-semibold text-gray-900 mb-3">{t('analytics_chart_age_dist', 'Age Distribution')}</h3>
                {audienceData?.age_ranges?.length > 0 ? (
                  <div className="space-y-3">
                    {(audienceData?.age_ranges || []).map((a: { range: string; percentage: number }) => (
                      <div key={a.range} className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-gray-600 w-16">{a.range}</span>
                        <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${a.percentage}%` }} />
                        </div>
                        <span className="text-xs font-bold text-gray-700 w-12 text-right">{a.percentage}%</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-8">{t('analytics_msg_no_age_data', 'No age data available yet')}</p>
                )}
              </div>
            </div>

            {/* Top Locations */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-xs font-semibold text-gray-900 mb-3">{t('analytics_chart_top_locations', 'Top Locations')}</h3>
              {audienceData?.top_locations?.length > 0 ? (
                <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                  {(audienceData?.top_locations || []).map((loc: { location: string; percentage: number }, i: number) => (
                    <div key={loc.location} className="flex items-center gap-3 py-2 border-b border-gray-50">
                      <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                      <Globe className="h-3.5 w-3.5 text-gray-400" />
                      <span className="text-xs font-semibold text-gray-700 flex-1">{loc.location}</span>
                      <span className="text-xs font-bold text-gray-900">{loc.percentage}%</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">{t('analytics_msg_no_location_data', 'No location data available yet. Audience demographics require Instagram Business or Facebook Page accounts.')}</p>
              )}
            </div>
          </div>
        )}

        {/* Stories Tab Content */}
        {activeTab === 'stories' && (
          <div className="space-y-3">
            {/* Stories Summary Cards */}
            <div className="grid grid-cols-4 gap-3">
              {storiesSummary.map((stat) => (
                  <div key={stat.label} className={`bg-white rounded-xl border border-gray-200 p-3 border-l-[3px] border-l-${stat.color}-500`}>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">{stat.label}</p>
                    <p className="text-lg font-bold text-gray-900">{stat.value.toLocaleString()}</p>
                  </div>
              ))}
            </div>

            {/* Stories Timeline Chart */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-xs font-semibold text-gray-900 mb-3">{t('analytics_chart_stories_time', 'Stories Over Time')}</h3>
              {storiesData?.timeline?.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={storiesData.timeline}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Area type="monotone" dataKey="views" stroke="#8b5cf6" fill="#8b5cf650" strokeWidth={2} name={t('analytics_label_views', 'Views')} />
                    <Area type="monotone" dataKey="replies" stroke="#10b981" fill="#10b98130" strokeWidth={2} name={t('analytics_label_replies', 'Replies')} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-gray-400 text-center py-12">{t('analytics_msg_no_stories', 'No stories published yet. Create stories from the Composer to see analytics here.')}</p>
              )}
            </div>

            {/* Top Stories */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-xs font-semibold text-gray-900 mb-3">{t('analytics_chart_top_stories', 'Top Stories')}</h3>
              {storiesData?.top_stories?.length > 0 ? (
                <div className="space-y-3">
                  {(storiesData?.top_stories || []).map((story: { id: string; media_url?: string; impressions?: number; replies?: number; exits?: number; text?: string; published_at?: string; views?: number; shares?: number }, i: number) => (
                    <div key={story.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100">
                      <span className="text-xs font-bold text-gray-400 w-5">#{i + 1}</span>
                      {story.media_url ? (
                        <img src={story.media_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                          <Film className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-700 truncate">{story.text || 'Story'}</p>
                        <p className="text-[10px] text-gray-400">{story.published_at ? new Date(story.published_at).toLocaleDateString() : ''}</p>
                      </div>
                      <div className="flex items-center gap-4 text-xs">
                        <div className="text-center">
                          <p className="font-bold text-gray-900">{(story.views || 0).toLocaleString()}</p>
                          <p className="text-[9px] text-gray-400">{t('analytics_label_views', 'views')}</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-gray-900">{story.replies || 0}</p>
                          <p className="text-[9px] text-gray-400">{t('analytics_label_replies_short', 'replies')}</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-gray-900">{story.shares || 0}</p>
                          <p className="text-[9px] text-gray-400">{t('analytics_label_shares_short', 'shares')}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">{t('analytics_msg_no_stories_data', 'No stories data yet')}</p>
              )}
            </div>
          </div>
        )}

        {/* Posts Tab Content */}
        {activeTab === 'posts' && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-xs font-semibold text-gray-900 mb-3">{t('analytics_chart_all_posts_perf', 'All Posts Performance')}</h3>
            {topPosts?.posts && topPosts.posts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="pb-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">#</th>
                      <th className="pb-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">{t('analytics_col_content', 'Content')}</th>
                      <th className="pb-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">{t('analytics_col_platform', 'Platform')}</th>
                      <th className="pb-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-right">{t('analytics_col_likes', 'Likes')}</th>
                      <th className="pb-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-right">{t('analytics_col_comments', 'Comments')}</th>
                      <th className="pb-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-right">{t('analytics_col_shares', 'Shares')}</th>
                      <th className="pb-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-right">{t('analytics_col_reach', 'Reach')}</th>
                      <th className="pb-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-right">{t('analytics_col_date', 'Date')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(topPosts?.posts || []).map((post: TopPost, i: number) => (
                      <tr key={post.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="py-3 text-xs font-bold text-gray-400">{i + 1}</td>
                        <td className="py-3 text-xs text-gray-700 max-w-[200px] truncate">{post.text}</td>
                        <td className="py-3">
                          <div className="flex gap-1">
                            {(post.platforms || []).map((p: string) => (
                              <span key={p} className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: PLATFORM_COLORS[p] + '20', color: PLATFORM_COLORS[p] }}>{p}</span>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 text-xs font-semibold text-gray-900 text-right">{(post.likes || 0).toLocaleString()}</td>
                        <td className="py-3 text-xs font-semibold text-gray-900 text-right">{(post.comments || 0).toLocaleString()}</td>
                        <td className="py-3 text-xs font-semibold text-gray-900 text-right">{(post.shares || 0).toLocaleString()}</td>
                        <td className="py-3 text-xs font-semibold text-gray-900 text-right">{(post.reach || 0).toLocaleString()}</td>
                        <td className="py-3 text-xs text-gray-500 text-right">{post.published_at ? new Date(post.published_at).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-12">{t('analytics_msg_no_published_posts', 'No published posts yet')}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
